/**
 * Gateway CRUD data layer — tenants / devices / orders against the API gateway (:5050)
 * via the `/api` Vite proxy. All calls carry the `x-tenant-id` header.
 *
 * Endpoints are best-effort against the live backend; missing ones surface as errors
 * the pages render gracefully (empty state / toast).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tokenStore } from '@monorepo/shared/api'
// Tenant is injected by the gateway from the JWT — the frontend must not set it.
const getCurrentTenantId = (): string | undefined => undefined

// Every endpoint below is called as `/api/...`, so a base that already ends in "/api" (as
// VITE_API_URL does, for the dev Vite proxy) would double it up (e.g. "/api/api/tenants").
// Strip a trailing "/api" here so this works whether VITE_API_URL is "/api" (dev proxy) or an
// absolute prod URL ending in "/api".
const RAW_API = (import.meta.env.VITE_API_URL as string) || '/api'
const API = RAW_API.replace(/\/api\/?$/, '')

/** Bearer token from the admin auth session, if the user is logged in. Was reading a
 * localStorage key ('auth-storage', shape `.state.token`) that the real login flow never
 * writes — shared-core's tokenStore persists under 'sv-tokens' as `{accessToken, ...}`
 * instead, so this always returned null and every call through `req()` went out with no
 * Authorization header, 401ing any manager-gated endpoint (e.g. Payments) unconditionally
 * regardless of being signed in. Reading the real store directly fixes it. */
function authToken(): string | null {
  return tokenStore.get()?.accessToken ?? null
}

// Resolves per-call (not a frozen const) so the header reflects whichever business is
// actually logged in, rather than one static tenant baked into the build — falls back to
// VITE_TENANT_ID only when nobody's logged in yet (pre-login calls, if any).
function currentTenant(): string {
  return getCurrentTenantId() ?? (import.meta.env.VITE_TENANT_ID as string) ?? 'default'
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = authToken()
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': currentTenant(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
  const text = await res.text()
  return (text ? JSON.parse(text) : null) as T
}

/** Unwrap the services' `BaseResponse { success, data, message }` envelope to its payload. */
function unwrap<T = any>(data: any): T {
  if (data && typeof data === 'object' && 'success' in data && 'data' in data) return data.data as T
  return data as T
}

/** Normalize the many list-response shapes the services return into a plain array. */
function asList<T = any>(input: any): T[] {
  // Unwrap the BaseResponse { success, data, message } envelope first.
  const data = input && typeof input === 'object' && 'success' in input && 'data' in input ? input.data : input
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.items)) return data.items
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.results)) return data.results
  return []
}

export const gateway = {
  get: <T>(p: string) => req<T>('GET', p),
  post: <T>(p: string, b?: unknown) => req<T>('POST', p, b),
  put: <T>(p: string, b?: unknown) => req<T>('PUT', p, b),
  del: <T>(p: string) => req<T>('DELETE', p),
}

// ---------- Tenants -------------------------------------------------------- //
export interface Tenant {
  id?: string
  name?: string
  status?: string
  plan?: string
  type?: string
  createdAt?: string
  [k: string]: any
}

export function useTenants() {
  return useQuery({
    queryKey: ['gw', 'tenants'],
    queryFn: async () => asList<Tenant>(await gateway.get('/api/tenants')),
  })
}
export function useCreateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Tenant>) => gateway.post('/api/tenants', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'tenants'] }),
  })
}
export function useUpdateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Tenant> }) => gateway.put(`/api/tenants/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'tenants'] }),
  })
}
export function useDeleteTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/tenants/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'tenants'] }),
  })
}

// ---------- Businesses / Branches (TenantService) --------------------------- //
// The generic onboarding hierarchy a business's IoT footprint lives under:
// Tenant -> Business -> Branch -> Device. Unlike most endpoints here, TenantService
// derives tenantId from the JWT (ignoring the body/header) for its own create calls,
// but still requires tenantId in the body — so it's included below to satisfy
// validation, not because it's actually used to scope the write.
export interface Business {
  id?: string
  tenantId?: string
  name?: string
  code?: string
  description?: string
  status?: string
  type?: string
  [k: string]: any
}

export interface Branch {
  id?: string
  tenantId?: string
  businessId?: string
  name?: string
  code?: string
  status?: string
  type?: string
  [k: string]: any
}

export function useBusinesses() {
  return useQuery({
    queryKey: ['gw', 'businesses'],
    queryFn: async () => asList<Business>(await gateway.get('/api/businesses')),
  })
}
export function useCreateBusiness() {
  const qc = useQueryClient()
  return useMutation({
    // Onboarding a new business is a SuperAdmin action (see BusinessesController's
    // [Authorize(Roles="SuperAdmin")]) — it needs its own brand-new tenantId, not the
    // SuperAdmin's own (a platform admin has no "tenant" of their own, and reusing one
    // static id here would collide every new business onto the same tenant).
    mutationFn: (body: Partial<Business>) => gateway.post('/api/businesses', { tenantId: crypto.randomUUID(), ...body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'businesses'] }),
  })
}

export interface SubscriptionDTO {
  tenantId: string
  plan?: string
  status?: string
  renewsAt?: string
}

// SuperAdmin-only platform-wide SaaS overview — GET /api/billing/subscriptions?tenantIds=...
// Backend has no subscriptions table yet, so this resolves each id via the same
// config-stub logic as the business's own GET /api/billing/subscription.
export function useSubscriptions(tenantIds: string[]) {
  return useQuery({
    queryKey: ['gw', 'subscriptions', tenantIds],
    queryFn: async () => asList<SubscriptionDTO>(await gateway.get(`/api/billing/subscriptions?tenantIds=${tenantIds.join(',')}`)),
    enabled: tenantIds.length > 0,
  })
}

// SuperAdmin activate/deactivate (and any other field edit) — PUT /api/businesses/{id},
// [Authorize(Roles="SuperAdmin")] on the backend.
export function useUpdateBusiness() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<Business> & { id: string }) => gateway.put(`/api/businesses/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'businesses'] }),
  })
}

export function useBranches(businessId?: string) {
  return useQuery({
    queryKey: ['gw', 'branches', businessId],
    queryFn: async () => asList<Branch>(await gateway.get(`/api/branch/business/${businessId}`)),
    enabled: !!businessId,
  })
}
export function useCreateBranch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Branch>) => gateway.post('/api/branch', { tenantId: currentTenant(), ...body }),
    onSuccess: (_data, variables) => qc.invalidateQueries({ queryKey: ['gw', 'branches', variables.businessId] }),
  })
}

// ---------- Devices -------------------------------------------------------- //
export interface Device {
  id?: string
  name?: string
  deviceType?: string
  type?: string
  status?: string
  [k: string]: any
}

export function useDevices() {
  return useQuery({
    queryKey: ['gw', 'devices'],
    queryFn: async () => asList<Device>(await gateway.get('/api/devices')),
  })
}

export interface DeviceType { deviceType: string; version?: number; updatedAt?: string }

/** Registered device-type schemas for this tenant (DeviceAPI's device_schemas), for a
 * device-onboarding type dropdown. */
export function useDeviceTypes() {
  return useQuery({
    queryKey: ['gw', 'device-types'],
    queryFn: async () => asList<DeviceType>(await gateway.get('/api/device-types')),
  })
}
export function useCreateDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Device>) => gateway.post('/api/devices', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'devices'] }),
  })
}
export function useUpdateDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Device> }) => gateway.put(`/api/devices/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'devices'] }),
  })
}
export function useDeleteDevice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/devices/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'devices'] }),
  })
}
export function useDeviceControl() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['gw', 'devices'] })
  return {
    start: useMutation({ mutationFn: (id: string) => gateway.post(`/api/devices/${id}/start`), onSuccess: invalidate }),
    stop: useMutation({ mutationFn: (id: string) => gateway.post(`/api/devices/${id}/stop`), onSuccess: invalidate }),
    command: useMutation({
      mutationFn: ({ id, command }: { id: string; command: Record<string, unknown> }) =>
        gateway.post(`/api/devices/${id}/command`, command),
    }),
  }
}

// ---------- Orders --------------------------------------------------------- //
export interface Order {
  id?: string
  customerName?: string
  email?: string
  total?: number
  status?: string
  createdAt?: string
  paymentStatus?: string
  paymentMethod?: string
  [k: string]: any
}

// GET /api/orders/business — every order across the whole tenant (any of the business's
// customers), not GET /api/orders (single-userId-scoped — would only ever show the
// logged-in business owner's own orders, not their customers').
export function usePlatformOrders() {
  return useQuery({
    queryKey: ['gw', 'orders'],
    queryFn: async () => asList<Order>(await gateway.get('/api/orders/business')),
  })
}
export function useUpdateOrderStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      gateway.put(`/api/orders/${id}/status`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'orders'] }),
  })
}
export function useCancelOrder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      gateway.post(`/api/orders/${id}/cancel`, { reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'orders'] }),
  })
}

// ---------- Products & Categories (CommerceService) ------------------------ //
export interface Product {
  id?: string
  name?: string
  description?: string
  price?: number
  categoryId?: string
  [k: string]: any
}
export interface Category { id?: string; name?: string; description?: string; [k: string]: any }

export function useProducts() {
  return useQuery({
    queryKey: ['gw', 'products'],
    queryFn: async () => asList<Product>(await gateway.get('/api/products?pageNumber=1&pageSize=100')),
  })
}
export function useCategories() {
  return useQuery({
    queryKey: ['gw', 'categories'],
    queryFn: async () => asList<Category>(await gateway.get('/api/categories')),
  })
}
export function useCreateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Product>) => gateway.post('/api/products', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'products'] }),
  })
}
export function useUpdateProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Product> }) => gateway.put(`/api/products/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'products'] }),
  })
}
export function useDeleteProduct() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/products/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'products'] }),
  })
}
export function useCreateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Category>) => gateway.post('/api/categories', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'categories'] }),
  })
}
export function useUpdateCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Category> }) => gateway.put(`/api/categories/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'categories'] }),
  })
}
export function useDeleteCategory() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'categories'] }),
  })
}

// ---------- Campaigns (CampaignService) ------------------------------------ //
export interface Campaign {
  id?: string
  name?: string
  description?: string
  type?: string
  status?: string
  startDate?: string
  endDate?: string
  discountPercentage?: number
  couponCode?: string
  [k: string]: any
}

export function useCampaigns() {
  return useQuery({
    queryKey: ['gw', 'campaigns'],
    queryFn: async () => asList<Campaign>(await gateway.get('/api/campaigns')),
  })
}
export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Campaign>) => gateway.post('/api/campaigns', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'campaigns'] }),
  })
}
export function useUpdateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Campaign> }) => gateway.put(`/api/campaigns/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'campaigns'] }),
  })
}
export function useDeleteCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/campaigns/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'campaigns'] }),
  })
}
export function useCampaignAction() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['gw', 'campaigns'] })
  return {
    launch: useMutation({ mutationFn: (id: string) => gateway.post(`/api/campaigns/${id}/launch`), onSuccess: invalidate }),
    close: useMutation({ mutationFn: (id: string) => gateway.post(`/api/campaigns/${id}/close`), onSuccess: invalidate }),
  }
}

// ---------- Support Tickets (SupportService) ------------------------------- //
export interface Ticket {
  id?: string
  ticketNumber?: string
  title?: string
  description?: string
  status?: string
  priority?: string
  category?: string
  type?: string
  assignedToName?: string
  commentCount?: number
  createdAt?: string
  [k: string]: any
}

export function useTickets() {
  return useQuery({
    queryKey: ['gw', 'tickets'],
    queryFn: async () => asList<Ticket>(await gateway.get('/api/tickets')),
  })
}
export function useCreateTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Ticket>) => gateway.post('/api/tickets', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'tickets'] }),
  })
}
export function useDeleteTicket() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/tickets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'tickets'] }),
  })
}
export function useTicketActions() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['gw', 'tickets'] })
  return {
    setStatus: useMutation({
      mutationFn: ({ id, status }: { id: string; status: string }) => gateway.post(`/api/tickets/${id}/status`, { status }),
      onSuccess: invalidate,
    }),
    resolve: useMutation({
      mutationFn: ({ id, notes }: { id: string; notes: string }) => gateway.post(`/api/tickets/${id}/resolve`, { notes }),
      onSuccess: invalidate,
    }),
    assign: useMutation({
      mutationFn: ({ id, assignedToId, assignedToName }: { id: string; assignedToId: string; assignedToName: string }) =>
        gateway.post(`/api/tickets/${id}/assign`, { assignedToId, assignedToName }),
      onSuccess: invalidate,
    }),
  }
}

// ---------- Contacts / CRM (ContactService) -------------------------------- //
export interface Contact {
  id?: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  subject?: string
  message?: string
  status?: string
  category?: string
  companyName?: string
  createdAt?: string
  [k: string]: any
}

export function useContacts() {
  return useQuery({
    queryKey: ['gw', 'contacts'],
    queryFn: async () => asList<Contact>(await gateway.get('/api/contacts')),
  })
}
export function useCreateContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Contact>) => gateway.post('/api/contacts', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'contacts'] }),
  })
}
export function useUpdateContactStatus() {
  const qc = useQueryClient()
  return useMutation({
    // ContactService takes status as a query param on PUT /api/contacts/{id}/status.
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      gateway.put(`/api/contacts/${id}/status?status=${encodeURIComponent(status)}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'contacts'] }),
  })
}
export function useDeleteContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/contacts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'contacts'] }),
  })
}

// ---------- Quotes (QuoteService) ------------------------------------------ //
export interface Quote {
  id?: string
  quoteNumber?: string
  status?: string
  customerName?: string
  customerEmail?: string
  validUntil?: string
  subTotal?: number
  total?: number
  grandTotal?: number
  [k: string]: any
}

export function useQuotes() {
  return useQuery({
    queryKey: ['gw', 'quotes'],
    queryFn: async () => asList<Quote>(await gateway.get('/api/quotes')),
  })
}
export function useCreateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Quote>) => gateway.post('/api/quotes', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'quotes'] }),
  })
}
export function useUpdateQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Quote> }) => gateway.put(`/api/quotes/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'quotes'] }),
  })
}
export function useDeleteQuote() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/quotes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'quotes'] }),
  })
}
export function useQuoteActions() {
  const qc = useQueryClient()
  const invalidate = () => qc.invalidateQueries({ queryKey: ['gw', 'quotes'] })
  return {
    send: useMutation({ mutationFn: (id: string) => gateway.post(`/api/quotes/${id}/send`), onSuccess: invalidate }),
    accept: useMutation({ mutationFn: (id: string) => gateway.post(`/api/quotes/${id}/accept`), onSuccess: invalidate }),
    reject: useMutation({ mutationFn: (id: string) => gateway.post(`/api/quotes/${id}/reject`), onSuccess: invalidate }),
  }
}

// ---------- Payments (PaymentService) --------------------------------------- //
export interface Payment {
  id?: string
  tenantId?: string
  orderId?: string
  amount?: number
  currency?: string
  status?: string
  method?: string
  transactionId?: string
  failureReason?: string
  createdAt?: string
  processedAt?: string
  refundedAt?: string
  [k: string]: any
}

// PaymentsController.List (BillingService) — /api/payments (no v1, no GET at all) never
// existed on the backend; this was 404ing unconditionally. Fixed to the real endpoint, added
// alongside the existing POST /refund routes specifically to back this page.
export function usePayments(pageNumber = 1, pageSize = 20) {
  return useQuery({
    queryKey: ['gw', 'payments', pageNumber, pageSize],
    queryFn: async () => asList<Payment>(await gateway.get(`/api/v1/payments?pageNumber=${pageNumber}&pageSize=${pageSize}`)),
  })
}
/** GET /api/v1/payments/webhooks (BillingService) — Stripe webhook health, built from the verified,
 * de-duplicated events it has processed. Signature failures are rejected before anything is stored,
 * so they never appear here. */
export interface WebhookStatus {
  secretConfigured: boolean
  lastEventAt: string | null
  last24hCount: number
  last7dCount: number
  byType: { type: string; count: number; lastAt: string }[]
  recent: { eventId: string; type: string; processedAt: string }[]
}
export function useWebhookStatus() {
  return useQuery({
    queryKey: ['gw', 'payments', 'webhooks'],
    queryFn: async () => (await gateway.get('/api/v1/payments/webhooks')) as WebhookStatus,
    refetchInterval: 30_000,
  })
}
export function useRefundPayment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, amount, reason }: { id: string; amount: number; reason: string }) =>
      gateway.post(`/api/v1/payments/${id}/refund`, { amount, reason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'payments'] }),
  })
}

// ---------- Automation Rules (RuleAPI) ------------------------------------- //
export interface Rule {
  id?: string
  name?: string
  definitionJson?: string
  enabled?: boolean
  createdAt?: string
  [k: string]: any
}

export function useRules() {
  return useQuery({
    queryKey: ['gw', 'rules'],
    queryFn: async () => asList<Rule>(await gateway.get('/api/rules')),
  })
}
export function useCreateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<Rule>) => gateway.post('/api/rules', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'rules'] }),
  })
}
export function useToggleRule() {
  const qc = useQueryClient()
  return useMutation({
    // RuleAPI expects a raw JSON boolean body on PUT /api/rules/{id}/enable.
    mutationFn: ({ id, enable }: { id: string; enable: boolean }) => gateway.put(`/api/rules/${id}/enable`, enable),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'rules'] }),
  })
}
export function useUpdateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<Rule> }) => gateway.put(`/api/rules/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'rules'] }),
  })
}
export function useDeleteRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => gateway.del(`/api/rules/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'rules'] }),
  })
}

// ---------- Support conversations (Business ↔ SuperAdmin chat) ------------ //
// Backed by VaultStore.IdentityService/Controllers/ReportsController.cs — a "report" here
// is a business's support conversation with the platform, not a farm/device report.
export interface SupportReport {
  id?: string
  tenantId?: string
  topic?: string
  details?: string
  category?: number
  status?: number
  riskLevel?: number
  adminNotes?: string
  submittedAt?: string
  [k: string]: any
}

export function useReports() {
  return useQuery({
    queryKey: ['gw', 'reports'],
    queryFn: async () => asList<SupportReport>(await gateway.get('/api/reports')),
    refetchInterval: 10000,
  })
}
export function useCreateReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: Partial<SupportReport>) => gateway.post('/api/reports', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'reports'] }),
  })
}
export function useUpdateReport() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: Partial<SupportReport> & { id: string }) => gateway.put(`/api/reports/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gw', 'reports'] }),
  })
}
